import { getArticle, getPathname } from 'common/selectors';
import {
  getDisplayedName,
  getDivisionId,
  getPrimaryPrice,
  getPrimaryPriceCb,
  getProductBelong,
  getProductBelongFromSource,
  getSubDivisionId,
} from 'features/product-info';
import { ReviewScoreTotal } from 'features/reviews';
import { getReviewsRatingTotal } from 'features/reviews/selectors';
import { call, select } from 'redux-saga/effects';
import { BasketFlowUpdateItemPayload } from 'features/basket';
import { EffectR } from 'types/utils';
import {
  getDeliveryTime,
  getMaxStockAvailable,
  getStocksInStores,
  StoreStockData,
} from 'features/goods-supply';
import { PriceMainProps } from 'common/components/product-price-view/types';
import { roundNumber } from 'utils';
import { NOT_SET } from '../utils';

export function* selectProductName() {
  return ((yield select(getDisplayedName)) as EffectR.Select<typeof getDisplayedName>) ?? NOT_SET;
}

export function* selectProductAvailability() {
  return ((yield select(getDeliveryTime)) as EffectR.Select<typeof getDeliveryTime>) ?? NOT_SET;
}

export function* selectProductId() {
  return ((yield select(getArticle)) as EffectR.Select<typeof getArticle>) ?? NOT_SET;
}

export function* selectProductPrice() {
  const primaryPrice: PriceMainProps = yield select(getPrimaryPrice);
  let productPrice = Number.isFinite(primaryPrice?.price) ? Number(primaryPrice.price).toFixed(2) : NOT_SET;
  return productPrice;
}

export function* selectProductPricePrevious() {
  const primaryPrice: PriceMainProps = yield select(getPrimaryPrice);
  let prev = Number.isFinite(primaryPrice?.prevPrice) ? Number(primaryPrice.prevPrice).toFixed(2) : null;
  return prev;
}

export function* selectProductDiscount() {
  const primaryPrice: PriceMainProps = yield select(getPrimaryPrice);
  return primaryPrice.discountPercent ?? null;
}

export function* selectProductBelong() {
  return (yield select(getProductBelong)) as EffectR.Select<typeof getProductBelong>;
}

export function* selectPagePath() {
  return ((yield select(getPathname)) as EffectR.Select<typeof getPathname>) ?? NOT_SET;
}

export function* selectDivision() {
  return ((yield select(getDivisionId)) as EffectR.Select<typeof getDivisionId>) ?? NOT_SET;
}

export function* selectSubDivision() {
  return ((yield select(getSubDivisionId)) as EffectR.Select<typeof getSubDivisionId>) ?? NOT_SET;
}

export function* selectReviewsTotalCount() {
  const totalScoreMeta: ReviewScoreTotal = yield select(getReviewsRatingTotal);
  return totalScoreMeta?.count?.toFixed(0) ?? NOT_SET;
}

export function* selectReviewsTotalScore() {
  const totalScoreMeta: ReviewScoreTotal = yield select(getReviewsRatingTotal);
  return totalScoreMeta?.score?.toFixed(2) ?? NOT_SET;
}

export function* selectVariantsReviewsTotal() {
  const totalScoreMeta: ReviewScoreTotal = yield select(getReviewsRatingTotal);
  const value = totalScoreMeta?.groupReviewCount ?? totalScoreMeta?.count;
  return value?.toFixed(0) ?? NOT_SET;
}

export function* selectVariantsRatingAvg() {
  const totalScoreMeta: ReviewScoreTotal = yield select(getReviewsRatingTotal);
  const value = totalScoreMeta?.groupScore ?? totalScoreMeta?.score;
  return value?.toFixed(2) ?? NOT_SET;
}

export function* selectProductInfo(context?: BasketFlowUpdateItemPayload['context']) {
  return {
    elementBlock: context?.elementBlock ?? 'productDetail',
    productName: context
      ? context.product.displayedName
      : ((yield call(selectProductName)) as EffectR.Call<typeof selectProductName>),
    productPrice: context
      ? String(getPrimaryPriceCb(context.product.price).price)
      : ((yield call(selectProductPrice)) as EffectR.Call<typeof selectProductPrice>),
    ...(context && { productPosition: String(context.productPositionFromOne) }),
    productBelong: context
      ? getProductBelongFromSource(context.product.source)
      : ((yield call(selectProductBelong)) as EffectR.Call<typeof selectProductBelong>),
  };
}

export function* selectProductAvailableQuantity() {
  const stocksInStores: StoreStockData[] = yield select(getStocksInStores);
  const maxStockAvailable: number | undefined = yield select(getMaxStockAvailable);

  if (stocksInStores.length > 0) {
    return roundNumber(
      stocksInStores.reduce((acc, curr) => acc + curr.stockValue, 0),
      2,
    );
  } else if (maxStockAvailable) {
    return maxStockAvailable;
  } else {
    return 0;
  }
}
